import React, { memo, useState } from 'react';
import PropTypes from 'prop-types';
import { NumericFormat } from 'react-number-format';
import { useDebouncedCallback } from 'use-debounce';
import useEffectOnUpdate from '../../hooks/useEffectOnUpdate';
import Field from './Field';
import NumberSteps from './NumberSteps';
import { formatNumber, roundToDecimals } from '../../utils';
import Constants from '../../utils/Constants';
import useEffectOnLoad from '../../hooks/useEffectOnLoad';
import { extendedStateType } from '../../types';
import { resolveValue } from '../../utils/fields';
import { NO_STATE } from '../../hooks/useExtendedState';

export const DEFAULT_STEP = 1;

NumberField.propTypes = {
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]),
    label: PropTypes.node,
    inputId: PropTypes.string,
    onChange: PropTypes.func,
    validator: PropTypes.func,
    onValidated: PropTypes.func,
    autoComplete: PropTypes.string,
    className: PropTypes.string,
    postfix: PropTypes.node,
    step: PropTypes.number,
    min: PropTypes.number,
    max: PropTypes.number,
    decrementDisabled: PropTypes.bool,
    incrementDisabled: PropTypes.bool,
    fractionDigits: PropTypes.number,
    onReset: PropTypes.func,
    isChanged: PropTypes.bool,
    isRequired: PropTypes.bool,
    noThousandSeparator: PropTypes.bool,
    disabled: PropTypes.bool,
    validateOnLoad: PropTypes.bool,
    validateOnValidatorChange: PropTypes.bool,
    roundToStepMultiple: PropTypes.bool,
    state: extendedStateType,
    valueTransformer: PropTypes.func,
};

function NumberField(props) { // NOSONAR javascript:S3776
    const {
        label = null,
        inputId = null,
        value,
        onChange,
        onValidated,
        autoComplete = null,
        className = '',
        validator = null,
        postfix = null,
        step = DEFAULT_STEP,
        min = null,
        max = null,
        decrementDisabled,
        incrementDisabled,
        fractionDigits,
        onReset,
        isChanged,
        isRequired = false,
        noThousandSeparator = false,
        disabled = false,
        validateOnLoad = false,
        validateOnValidatorChange = false,
        roundToStepMultiple = false,
        state = NO_STATE,
        valueTransformer,
    } = props;
    const [isInModification, setInModification] = useState(false);
    const [error, setError] = useState(null);
    const stateValueSetter = state && state !== NO_STATE ? state.setValueIfNew : () => {};
    const resolvedValue = resolveValue(value, state);
    const [displayedValue, setDisplayedValue] = useState(resolvedValue !== null ? formatNumber(resolvedValue, fractionDigits) : null);
    const debouncedValidation = useDebouncedCallback((valueForValidation) => {
        if (!disabled) {
            const possibleUpdatedValue = parseFloat(valueForValidation);
            const sanitizedValue = Number.isNaN(possibleUpdatedValue) ? null : possibleUpdatedValue;
            validator && validator(Number.isNaN(possibleUpdatedValue) ? '' : String(possibleUpdatedValue))
                .then(() => onValidationComplete(null, sanitizedValue))
                .catch(e => onValidationComplete(e, sanitizedValue));
        } else {
            setError(null);
        }
    }, Constants.DISPLAY_FIELD_ERROR_DELAY_IN_MS);
    const setNewValue = (newStringValue) => {
        if (newStringValue !== displayedValue) {
            let newSanitizedValue = parseFloat(newStringValue);
            let newSanitizedStringValue = newStringValue;
            if (newStringValue !== '-') {
                if (Number.isNaN(newSanitizedValue)) {
                    newSanitizedValue = null;
                    newSanitizedStringValue = '';
                }
            }

            setDisplayedValue(newSanitizedStringValue);
            const resolvedSanitizedValue = valueTransformer ? valueTransformer(newSanitizedValue) : newSanitizedValue;
            stateValueSetter(resolvedSanitizedValue);
            onChange && onChange(resolvedSanitizedValue);

            if (validator) {
                debouncedValidation(newSanitizedStringValue);
            } else {
                onValidationComplete(null, resolvedSanitizedValue);
            }
        }
    };
    const onValidationComplete = (newError, sanitizedValue) => {
        if (!isInModification && !newError) {
            setDisplayedValue(formatNumber(sanitizedValue, fractionDigits));
        }

        onValidated && onValidated(sanitizedValue, validator === null ? null : !newError);
        setError(newError);
    };
    useEffectOnUpdate(() => {
        if (!isInModification) {
            const resolvedValue = resolveValue(value, state);
            setNewValue(String(resolvedValue));
        }
    }, [value, state.value]);
    useEffectOnUpdate(() => {
        if (validateOnValidatorChange) {
            const newResolvedValue = resolveValue(value, state);
            debouncedValidation(newResolvedValue);
        }
    }, [validator]);
    useEffectOnLoad(() => {
        if (validateOnLoad) {
            const newResolvedValue = resolveValue(value, state);
            debouncedValidation(newResolvedValue);
        }
    });
    const onChangeHandler = (values) => setNewValue(values.value);
    const onButtonChangeHandler = (newValue) => {
        let possibleNewValue = newValue;
        if (min !== null && newValue < min) {
            possibleNewValue = min;
        }
        if (max !== null && newValue > max) {
            possibleNewValue = max;
        }
        setNewValue(formatNumber(String(possibleNewValue), fractionDigits));
    };
    const onFocusHandler = () => {
        setInModification(true);
    };
    const onBlurHandler = () => {
        if (!error && fractionDigits) {
            const possibleNewValue = parseFloat(displayedValue);
            let newValue = displayedValue;
            if (Number.isNaN(possibleNewValue)) {
                newValue = '';
            } else {
                const splitCurrentValue = displayedValue.trim().split('.');
                const decimalsCount = splitCurrentValue.length > 1 ? splitCurrentValue[1].length : 0;
                if (decimalsCount <= fractionDigits) {
                    newValue = String(possibleNewValue.toFixed(fractionDigits));
                }
            }
            setNewValue(newValue);
        }
        setInModification(false);
    };
    const doDecrement = () => {
        const parsedCurrentValue = parseFloat(displayedValue);
        const currentNumberValue = Number.isNaN(parsedCurrentValue) ? 0 : parsedCurrentValue;
        let newValue;
        if (roundToStepMultiple && currentNumberValue % step !== 0) {
            newValue = Math.floor(currentNumberValue / step) * step;
        } else {
            newValue = roundToDecimals(currentNumberValue - step, fractionDigits);
        }
        onButtonChangeHandler(newValue);
    };
    const doIncrement = () => {
        const parsedCurrentValue = parseFloat(displayedValue);
        const currentNumberValue = Number.isNaN(parsedCurrentValue) ? 0 : parsedCurrentValue;
        let newValue;
        if (roundToStepMultiple && currentNumberValue % step !== 0) {
            newValue = (Math.floor(currentNumberValue / step) + 1) * step;
        } else {
            newValue = roundToDecimals(currentNumberValue + step, fractionDigits);
        }
        onButtonChangeHandler(newValue);
    };
    let decrementDisabledInner;
    if (typeof decrementDisabled === 'undefined') {
        decrementDisabledInner = resolvedValue !== null && min !== null && resolvedValue <= min;
    } else {
        decrementDisabledInner = decrementDisabled;
    }
    let incrementDisabledInner;
    if (typeof incrementDisabled === 'undefined') {
        incrementDisabledInner = resolvedValue !== null && max !== null && resolvedValue >= max;
    } else {
        incrementDisabledInner = incrementDisabled;
    }
    const onKeyUp = (event) => {
        if (event.key === 'ArrowDown' && !decrementDisabledInner) {
            doDecrement();
        } else if (event.key === 'ArrowUp' && !incrementDisabledInner) {
            doIncrement();
        }
    };

    return (
        <Field
            label={label}
            postfix={postfix}
            inputId={inputId}
            error={error}
            onReset={onReset}
            isChanged={isChanged}
            state={state}
            isRequired={isRequired}
            isRequiredEmpty={isRequired && !displayedValue}
            disabled={disabled}
        >
            <NumberSteps
                incrementDisabled={incrementDisabledInner}
                decrementDisabled={decrementDisabledInner}
                onIncrement={doIncrement}
                onDecrement={doDecrement}
                incrementTitle={`+${step}`}
                decrementTitle={`-${step}`}
                disabled={disabled}
            >
                <NumericFormat
                    id={inputId}
                    className={className}
                    value={displayedValue || ''}
                    onKeyUp={onKeyUp}
                    onValueChange={onChangeHandler}
                    onBlur={onBlurHandler}
                    onFocus={onFocusHandler}
                    autoComplete={autoComplete}
                    aria-required={isRequired}
                    thousandSeparator={noThousandSeparator ? undefined : ' '}
                    isNumericString
                    disabled={disabled}
                />
            </NumberSteps>
        </Field>
    );
}

export default memo(NumberField);
